import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import axiosInstance from "@/lib/axios";
import { paymentEndpoint } from "@/config/endpoints";

/**
 * Student payment receipts (server: core/payments/receipts). A receipt is
 * numbered the first time it is downloaded or emailed; asking again returns
 * the same one.
 */
export interface ReceiptConfig {
  prefix: string;
  title: string;
  legalName: string | null;
  address: string | null;
  gstin: string | null;
  email: string | null;
  phone: string | null;
  footerNote: string | null;
  fallbackName: string;
  fallbackAddress: string | null;
}

interface IssuedReceipt {
  id: number;
  number: string;
  emailedAt: string | null;
  emailedTo: string | null;
}

const CONFIG_KEY = ["receipt-config"];

export function useReceiptConfig() {
  return useQuery<ReceiptConfig>({
    queryKey: CONFIG_KEY,
    queryFn: async () => (await axiosInstance.get(`${paymentEndpoint}/receipts/config`)).data.config,
  });
}

export function useSaveReceiptConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<ReceiptConfig>): Promise<ReceiptConfig> =>
      (await axiosInstance.put(`${paymentEndpoint}/receipts/config`, body)).data.config,
    onSuccess: (saved) => {
      qc.setQueryData(CONFIG_KEY, saved);
      toast.success("Receipt details saved");
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? "Failed to save receipt details"),
  });
}

async function issue(paymentId: number): Promise<IssuedReceipt> {
  return (await axiosInstance.post(`${paymentEndpoint}/${paymentId}/receipt`)).data.receipt;
}

/** Numbers the payment's receipt (if new) and downloads its PDF. */
export function useDownloadReceipt() {
  return useMutation({
    mutationFn: async (paymentId: number) => {
      const receipt = await issue(paymentId);
      const res = await axiosInstance.get(`${paymentEndpoint}/receipts/${receipt.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${receipt.number.replace(/[^A-Za-z0-9-]+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      return receipt;
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? "Couldn't create the receipt"),
  });
}

/** Numbers the payment's receipt (if new) and emails it to the lead. */
export function useEmailReceipt() {
  return useMutation({
    mutationFn: async (paymentId: number) => {
      const receipt = await issue(paymentId);
      return (await axiosInstance.post(`${paymentEndpoint}/receipts/${receipt.id}/email`, {})).data as {
        to: string;
        number: string;
      };
    },
    onSuccess: (sent) => toast.success(`Receipt ${sent.number} emailed to ${sent.to}`),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? "Couldn't email the receipt"),
  });
}
